import {
  AfterViewInit,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { merge } from 'rxjs/observable/merge';
import { debounceTime, filter, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';

import { NgSelectConfig } from './config.service';
import { ConsoleService } from './console.service';
import { DropdownPosition } from './dropdown-position-type';
import { newId } from './id';
import { ItemsList } from './items-list';
import { KeyCode, NgOption } from './latam-it-search-input-select.types';
import { NgDropdownPanelComponent } from './ng-dropdown-panel.component';
import { NgOptionComponent } from './ng-option.component';
import {
  NgFooterTemplateDirective,
  NgHeaderTemplateDirective,
  NgLabelTemplateDirective,
  NgLoadingTextTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgNotFoundTemplateDirective,
  NgOptgroupTemplateDirective,
  NgOptionTemplateDirective,
  NgTagTemplateDirective,
  NgTypeToSearchTemplateDirective
} from './ng-templates.directive';
import { SelectionModelFactory } from './selection-model';
import { isDefined, isFunction, isObject, isPromise } from './value-utils';

export const SELECTION_MODEL_FACTORY = new InjectionToken<SelectionModelFactory>(
  'latam-it-search-input-select-selection-model'
);
export type AddTagFn = ((term: string) => any | Promise<any>);
export type CompareWithFn = (a: any, b: any) => boolean;

@Component({
  selector: 'latam-it-search-input-select',
  templateUrl: './latam-it-search-input-select.component.html',
  styleUrls: ['./latam-it-search-input-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LatamItSearchInputSelectComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    role: 'listbox',
    class: 'latam-it-search-input-select',
    '[class.latam-it-search-input-select-single]': '!multiple'
  }
})
export class LatamItSearchInputSelectComponent implements OnDestroy, OnChanges, AfterViewInit, ControlValueAccessor, OnInit {
  // inputs
  @Input() items: any[] = [];
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() clearable = true;
  @Input() markFirst = true;
  @Input() placeholder: string;
  @Input() notFoundText: string;
  @Input() typeToSearchText: string;
  @Input() addTagText: string;
  @Input() loadingText: string;
  @Input() clearAllText: string;
  @Input() dropdownPosition: DropdownPosition = 'auto';
  @Input() appendTo: string;
  @Input() loading = false;
  @Input() closeOnSelect = true;
  @Input() hideSelected = false;
  @Input() selectOnTab = false;
  @Input() openOnEnter: boolean;
  @Input() maxSelectedItems: number;
  @Input() groupBy: string | Function;
  @Input() groupValue: Function;
  @Input() bufferAmount = 4;
  @Input() virtualScroll: boolean;
  @Input() selectableGroup = false;
  @Input() selectableGroupAsModel = true;
  @Input() searchFn = null;
  @Input() excludeGroupsFromDefaultSelection = false;
  @Input() clearOnBackspace = true;

  @Input() labelForId = null;
  @Input() @HostBinding('class.latam-it-search-input-select-typeahead') typeahead: Subject<string>;
  @Input() @HostBinding('class.latam-it-search-input-select-multiple') multiple = false;
  @Input() @HostBinding('class.latam-it-search-input-select-taggable') addTag: boolean | AddTagFn = false;
  @Input() @HostBinding('class.latam-it-search-input-select-searchable') searchable = true;
  @Input() @HostBinding('class.latam-it-search-input-select-opened') isOpen = false;

  /**
   * Identificação do componente
   */
  @Input() id: string;

  /**
   * Texto que será mostrado como label do select
   */
  @Input() label: string;

  /**
   * Campo com erro
   */
  @Input() error = false;
  @Input() filterValue: string;

  @Input()
  get compareWith() {
    return this._compareWith;
  }
  set compareWith(fn: CompareWithFn) {
    if (!isFunction(fn)) {
      throw Error('`compareWith` must be a function.');
    }
    this._compareWith = fn;
  }

  @Input()
  get clearSearchOnAdd() {
    return isDefined(this._clearSearchOnAdd) ? this._clearSearchOnAdd : this.closeOnSelect;
  }
  set clearSearchOnAdd(value) {
    this._clearSearchOnAdd = value;
  }

  // output events
  @Output() blurEvent = new EventEmitter();
  @Output() focusEvent = new EventEmitter();
  @Output() selectEvent = new EventEmitter();
  @Output() changeEvent = new EventEmitter();
  @Output() openEvent = new EventEmitter();
  @Output() closeEvent = new EventEmitter();
  @Output() searchEvent = new EventEmitter();
  @Output() clearEvent = new EventEmitter();
  @Output() addEvent = new EventEmitter();
  @Output() removeEvent = new EventEmitter();
  @Output() scroll = new EventEmitter<{ start: number; end: number }>();
  @Output() scrollToEnd = new EventEmitter();

  // custom templates
  @ContentChild(NgOptionTemplateDirective, { read: TemplateRef, static: true }) optionTemplate: TemplateRef<any>;
  @ContentChild(NgOptgroupTemplateDirective, { read: TemplateRef, static: true }) optgroupTemplate: TemplateRef<any>;
  @ContentChild(NgLabelTemplateDirective, { read: TemplateRef, static: true }) labelTemplate: TemplateRef<any>;
  @ContentChild(NgMultiLabelTemplateDirective, { read: TemplateRef, static: true }) multiLabelTemplate: TemplateRef<any>;
  @ContentChild(NgHeaderTemplateDirective, { read: TemplateRef, static: true }) headerTemplate: TemplateRef<any>;
  @ContentChild(NgFooterTemplateDirective, { read: TemplateRef, static: true }) footerTemplate: TemplateRef<any>;
  @ContentChild(NgNotFoundTemplateDirective, { read: TemplateRef, static: true }) notFoundTemplate: TemplateRef<any>;
  @ContentChild(NgTypeToSearchTemplateDirective, { read: TemplateRef, static: true }) typeToSearchTemplate: TemplateRef<any>;
  @ContentChild(NgLoadingTextTemplateDirective, { read: TemplateRef, static: true }) loadingTextTemplate: TemplateRef<any>;
  @ContentChild(NgTagTemplateDirective, { read: TemplateRef, static: true }) tagTemplate: TemplateRef<any>;

  @ViewChild(forwardRef(() => NgDropdownPanelComponent), { static: true }) dropdownPanel: NgDropdownPanelComponent;
  @ContentChildren(NgOptionComponent, { descendants: true }) ngOptions: QueryList<NgOptionComponent>;
  @ViewChild('filterInput', { static: true }) filterInput: ElementRef;

  @HostBinding('class.latam-it-search-input-select-disabled') disabled = false;
  @HostBinding('class.latam-it-search-input-select-filtered') get filtered() {
    return !!this.filterValue && this.searchable;
  }

  itemsList: ItemsList;
  viewPortItems: NgOption[] = [];
  dropdownId = newId();
  selectedItemId = 0;
  element: HTMLElement;
  focused: boolean;

  private _defaultLabel = 'label';
  private _primitive = true;
  private _manualOpen: boolean;
  private _pressedKeys: string[] = [];
  private _compareWith: CompareWithFn;
  private _clearSearchOnAdd: boolean;

  optionSelected: any;

  /**
   * Item selecionado
   * ```js
   *   // Exemplo
   *   itemSelectedValue = {value: 1}
   * ```
   * Obs.: É obrigatório ter value
   */
  @Input() itemSelectedValue = { value: null };

  private readonly _destroy$ = new Subject<void>();
  private readonly _keyPress$ = new Subject<string>();

  constructor(
    @Attribute('class') public classes: string,
    @Attribute('tabindex') public tabIndex: string,
    @Attribute('autofocus') private autoFocus: any,
    config: NgSelectConfig,
    @Inject(SELECTION_MODEL_FACTORY) newSelectionModel: SelectionModelFactory,
    _elementRef: ElementRef,
    private _cd: ChangeDetectorRef,
    private _console: ConsoleService
  ) {
    this._mergeGlobalConfig(config);
    this.itemsList = new ItemsList(this, newSelectionModel());
    this.element = _elementRef.nativeElement;
  }

  get selectedItems(): NgOption[] {
    return this.itemsList.selectedItems;
  }

  get selectedValues() {
    return this.selectedItems.map(x => x.value);
  }

  get hasValue() {
    return this.selectedItems.length > 0;
  }

  ngOnInit() {
    this._handleKeyPresses();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.multiple) {
      this.itemsList.clearSelected();
    }
    if (changes.items) {
      this._setItems(changes.items.currentValue || []);
    }
    if (changes.isOpen) {
      this._manualOpen = isDefined(changes.isOpen.currentValue);
    }
  }

  ngAfterViewInit() {
    if (this.items && this.items.length === 0) {
      this._setItemsFromNgOptions();
    }

    if (isDefined(this.autoFocus)) {
      this.focus();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  clearItem = (item: any) => {
    const option = this.selectedItems.find(x => x.value === item);
    this.unselect(option);
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown($event: KeyboardEvent) {
    // tslint:disable-next-line: deprecation
    if (KeyCode[$event.which]) {
      // tslint:disable-next-line: deprecation
      switch ($event.which) {
        case KeyCode.ArrowDown:
          this._handleArrowDown($event);
          break;
        case KeyCode.ArrowUp:
          this._handleArrowUp($event);
          break;
        case KeyCode.Space:
          this._handleSpace($event);
          break;
        case KeyCode.Enter:
          this._handleEnter($event);
          break;
        case KeyCode.Tab:
          this._handleTab($event);
          break;
        case KeyCode.Esc:
          this.close();
          $event.preventDefault();
          $event.stopPropagation();
          break;
        case KeyCode.Backspace:
          this._handleBackspace($event);
          break;
      }
    } else if ($event.key && $event.key.length === 1) {
      this._keyPress$.next($event.key.toLocaleLowerCase());
    }
  }

  handleMousedown($event: MouseEvent) {
    const target = $event.target as HTMLElement;
    if (target.tagName !== 'INPUT') {
      $event.preventDefault();
    }
    $event.stopPropagation();

    if (target.classList.contains('ng-clear-wrapper')) {
      this.handleClearClick();
      return;
    }

    if (target.classList.contains('ng-arrow-wrapper')) {
      this.handleArrowClick();
      return;
    }

    if (target.classList.contains('ng-value-icon')) {
      return;
    }

    if (!this.focused) {
      this.focus();
    }

    if (this.searchable) {
      this.open();
    } else {
      this.toggle();
    }
  }

  handleArrowClick() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleClearClick() {
    if (this.hasValue) {
      this.clearModel();
    }
    this._clearSearch();
    this.focus();
    if (this._isTypeahead) {
      this.typeahead.next(null);
    }
    this.clearEvent.emit();
  }

  clearModel() {
    if (!this.clearable) {
      return;
    }
    this.itemsList.clearSelected();
    this._updateNgModel();
  }

  writeValue(value: any | any[]): void {
    this.itemsList.clearSelected();
    this._handleWriteValue(value);
    this._cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cd.markForCheck();
  }

  toggle() {
    if (!this.isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    if (this.disabled || this.isOpen || this.itemsList.maxItemsSelected || this._manualOpen) {
      return;
    }

    if (!this._isTypeahead && !this.addTag && this.itemsList.noItemsToSelect) {
      return;
    }
    this.isOpen = true;
    this.itemsList.markSelectedOrDefault(this.markFirst);
    this.openEvent.emit();
    if (!this.filterValue) {
      this.focus();
    }
    this.detectChanges();
  }

  close() {
    if (!this.isOpen || this._manualOpen) {
      return;
    }
    this.isOpen = false;
    this._clearSearch();
    this._onTouched();
    this.closeEvent.emit();
    this._cd.markForCheck();
  }

  toggleItem(item: NgOption, $event: any) {
    if (!item || item.disabled || this.disabled) {
      return;
    }

    if (this.multiple && item.selected) {
      this.unselect(item);
    } else {
      this.select(item);
    }
  }

  select(item: NgOption) {
    if (!item.selected) {
      this.itemsList.select(item);
      if (this.clearSearchOnAdd) {
        this._clearSearch();
      }

      if (this.multiple) {
        this.addEvent.emit(item.value);
      }
      this._updateNgModel();
    }

    if (this.closeOnSelect || this.itemsList.noItemsToSelect) {
      this.close();
    }
    this.filterInput.nativeElement.blur();
    this.selectEvent.emit(item.value);
  }

  focus() {
    this.filterInput.nativeElement.focus();
  }

  unselect(item: NgOption) {
    this.itemsList.unselect(item);
    this.focus();
    this._updateNgModel();
    this.removeEvent.emit(item);
  }

  selectTag($event: any) {
    let tag;
    if (isFunction(this.addTag)) {
      tag = (this.addTag as AddTagFn)(this.filterValue);
    } else {
      tag = this._primitive ? this.filterValue : { [this.bindLabel]: this.filterValue };
    }

    const handleTag = item =>
      this._isTypeahead || !this.isOpen ? this.itemsList.mapItem(item, null) : this.itemsList.addItem(item);
    if (isPromise(tag)) {
      tag.then(item => this.select(handleTag(item))).catch(() => null);
    } else if (tag) {
      this.select(handleTag(tag));
    }
  }

  showClear() {
    return this.clearable && (this.hasValue || this.filterValue) && !this.disabled;
  }

  get showAddTag() {
    if (!this.filterValue) {
      return false;
    }

    const term = this.filterValue.toLowerCase();
    return (
      this.addTag &&
      (!this.itemsList.filteredItems.some(x => x.label.toLowerCase() === term) &&
        ((!this.hideSelected && this.isOpen) || !this.selectedItems.some(x => x.label.toLowerCase() === term))) &&
      !this.loading
    );
  }

  showNoItemsFound() {
    const empty = this.itemsList.filteredItems.length === 0;
    return (
      ((empty && !this._isTypeahead && !this.loading) || (empty && this._isTypeahead && this.filterValue && !this.loading)) &&
      !this.showAddTag
    );
  }

  showTypeToSearch() {
    const empty = this.itemsList.filteredItems.length === 0;
    return empty && this._isTypeahead && !this.filterValue && !this.loading;
  }

  filter(term: string) {
    this.filterValue = term;
    this.open();

    if (this._isTypeahead) {
      this.typeahead.next(this.filterValue);
    } else {
      this.itemsList.filter(this.filterValue);
      if (this.isOpen) {
        this.itemsList.markSelectedOrDefault(this.markFirst);
      }
    }

    this.searchEvent.emit(term);
  }

  onInputFocus($event) {
    if (this.focused) {
      return;
    }

    this.element.classList.add('latam-it-search-input-select-focused');
    this.focusEvent.emit($event);
    this.focused = true;
  }

  onInputBlur($event) {
    this.element.classList.remove('latam-it-search-input-select-focused');
    this.blurEvent.emit($event);
    if (!this.isOpen && !this.disabled) {
      this._onTouched();
    }
    this.focused = false;
  }

  onItemHover(item: NgOption) {
    if (item.disabled) {
      return;
    }
    this.itemsList.markItem(item);
  }

  detectChanges() {
    if (!(this._cd as any).destroyed) {
      this._cd.detectChanges();
    }
  }

  updateDropdownPosition() {
    if (this.dropdownPanel) {
      this.dropdownPanel.updateDropdownPosition();
    }
  }

  private _onChange = (_: any) => null;
  private _onTouched = () => null;

  private _setItems(items: any[]) {
    const firstItem = items[0];
    this.bindLabel = this.bindLabel || this._defaultLabel;
    this._primitive = isDefined(firstItem) ? !isObject(firstItem) : this._primitive;
    this.itemsList.setItems(items);
    if (items.length > 0 && this.hasValue) {
      this.itemsList.mapSelectedItems();
    }
    if (this.isOpen && isDefined(this.filterValue) && !this._isTypeahead) {
      this.itemsList.filter(this.filterValue);
    }
    if (this._isTypeahead || this.isOpen) {
      this.itemsList.markSelectedOrDefault(this.markFirst);
    }
  }

  private _setItemsFromNgOptions() {
    const handleNgOptions = (options: QueryList<NgOptionComponent>) => {
      this.items = options.map(option => ({
        $ngOptionValue: option.value,
        $ngOptionLabel: option.elementRef.nativeElement.innerHTML,
        disabled: option.disabled
      }));
      this.itemsList.setItems(this.items);
      if (this.hasValue) {
        this.itemsList.mapSelectedItems();
      }
      this.detectChanges();
    };

    const handleOptionChange = () => {
      const changedOrDestroyed = merge(this.ngOptions.changes, this._destroy$);
      merge(...this.ngOptions.map(option => option.stateChange$))
        .pipe(takeUntil(changedOrDestroyed))
        .subscribe((option: any) => {
          const item = this.itemsList.findItem(option.value);
          item.disabled = option.disabled;
          this._cd.markForCheck();
        });
    };

    this.ngOptions.changes
      .pipe(
        startWith(this.ngOptions),
        takeUntil(this._destroy$),
        filter((items: QueryList<any>) => !!items.length)
      )
      .subscribe(options => {
        this.bindLabel = this._defaultLabel;
        handleNgOptions(options);
        handleOptionChange();
      });
  }

  private _isValidWriteValue(value: any): boolean {
    if (!isDefined(value) || (this.multiple && value === '') || (Array.isArray(value) && value.length === 0)) {
      return false;
    }

    const validateBinding = (item: any): boolean => {
      if (!isDefined(this.compareWith) && isObject(item) && this.bindValue) {
        this._console.warn(`Binding object(${JSON.stringify(item)}) with bindValue is not allowed.`);
        return false;
      }
      return true;
    };

    if (this.multiple) {
      if (!Array.isArray(value)) {
        this._console.warn('Multiple select ngModel should be array.');
        return false;
      }
      return value.every(item => validateBinding(item));
    }
    return validateBinding(value);
  }

  private _handleWriteValue(ngModel: any | any[]) {
    if (!this._isValidWriteValue(ngModel)) {
      return;
    }

    const select = (val: any) => {
      let item = this.itemsList.findItem(val);
      // tslint:disable-next-line: early-exit
      if (item) {
        this.itemsList.select(item);
      } else {
        const isValObject = isObject(val);
        const isPrimitive = !isValObject && !this.bindValue;
        if (isValObject || isPrimitive) {
          this.itemsList.select(this.itemsList.mapItem(val, null));
        } else if (this.bindValue) {
          item = {
            [this.bindLabel]: null,
            [this.bindValue]: val
          };
          this.itemsList.select(this.itemsList.mapItem(item, null));
        }
      }
    };

    if (this.multiple) {
      (ngModel as any[]).forEach(item => select(item));
    } else {
      select(ngModel);
    }
  }

  private _handleKeyPresses() {
    if (this.searchable) {
      return;
    }

    this._keyPress$
      .pipe(
        takeUntil(this._destroy$),
        tap((letter: any) => this._pressedKeys.push(letter)),
        debounceTime(200),
        filter(() => this._pressedKeys.length > 0),
        map(() => this._pressedKeys.join(''))
      )
      .subscribe(term => {
        const item = this.itemsList.findByLabel(term);
        if (item) {
          if (this.isOpen) {
            this.itemsList.markItem(item);
            this._cd.markForCheck();
          } else {
            this.select(item);
          }
        }
        this._pressedKeys = [];
      });
  }

  private _updateNgModel() {
    const model = [];
    for (const item of this.selectedItems) {
      // tslint:disable-next-line: early-exit
      if (this.bindValue) {
        let value = null;
        if (item.children) {
          value = item.value[this.groupBy as string];
        } else {
          value = this.itemsList.resolveNested(item.value, this.bindValue);
        }
        model.push(value);
      } else {
        model.push(item.value);
      }
    }

    const selected = this.selectedItems.map(x => x.value);
    if (this.multiple) {
      this._onChange(model);
    } else {
      this._onChange(isDefined(model[0]) ? model[0] : null);
      this.changeEvent.emit(selected[0]);
    }

    this._cd.markForCheck();
  }

  private _clearSearch() {
    if (!this.filterValue) {
      return;
    }

    this.filterValue = null;
    this.itemsList.resetFilteredItems();
  }

  private _scrollToMarked() {
    if (!this.isOpen || !this.dropdownPanel) {
      return;
    }
    this.dropdownPanel.scrollInto(this.itemsList.markedItem);
  }

  private _scrollToTag() {
    if (!this.isOpen || !this.dropdownPanel) {
      return;
    }
    this.dropdownPanel.scrollIntoTag();
  }

  private _handleTab($event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }
    // tslint:disable-next-line: early-exit
    if (this.selectOnTab) {
      if (this.itemsList.markedItem) {
        this.toggleItem(this.itemsList.markedItem, $event);
        $event.preventDefault();
      } else if (this.showAddTag) {
        this.selectTag($event);
        $event.preventDefault();
      } else {
        this.close();
      }
    } else {
      this.close();
    }
  }

  private _handleEnter($event: KeyboardEvent) {
    if (this.isOpen || this._manualOpen) {
      if (this.itemsList.markedItem) {
        this.toggleItem(this.itemsList.markedItem, $event);
      } else if (this.showAddTag) {
        this.selectTag($event);
      }
    } else if (this.openOnEnter) {
      this.open();
    } else {
      return;
    }

    $event.preventDefault();
    $event.stopPropagation();
  }

  private _handleSpace($event: KeyboardEvent) {
    if (this.isOpen || this._manualOpen) {
      return;
    }
    this.open();
    $event.preventDefault();
  }

  private _handleArrowDown($event: KeyboardEvent) {
    if (this._nextItemIsTag(+1)) {
      this.itemsList.unmarkItem();
      this._scrollToTag();
    } else {
      this.itemsList.markNextItem();
      this._scrollToMarked();
    }
    this.open();
    $event.preventDefault();
  }

  private _handleArrowUp($event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }

    if (this._nextItemIsTag(-1)) {
      this.itemsList.unmarkItem();
      this._scrollToTag();
    } else {
      this.itemsList.markPreviousItem();
      this._scrollToMarked();
    }
    $event.preventDefault();
  }

  private _nextItemIsTag(nextStep: number): boolean {
    const nextIndex = this.itemsList.markedIndex + nextStep;
    return (
      this.addTag &&
      this.filterValue &&
      this.itemsList.markedItem &&
      (nextIndex < 0 || nextIndex === this.itemsList.filteredItems.length)
    );
  }

  private _handleBackspace($event: any) {
    if (this.filterValue || !this.clearable || !this.clearOnBackspace || !this.hasValue) {
      return;
    }

    if (this.multiple) {
      this.unselect(this.itemsList.lastSelectedItem);
    } else {
      this.clearModel();
    }
  }

  private get _isTypeahead() {
    return this.typeahead && this.typeahead.observers.length > 0;
  }

  private _mergeGlobalConfig(config: NgSelectConfig) {
    this.placeholder = this.placeholder || config.placeholder;
    this.notFoundText = this.notFoundText || config.notFoundText;
    this.typeToSearchText = this.typeToSearchText || config.typeToSearchText;
    this.addTagText = this.addTagText || config.addTagText;
    this.loadingText = this.loadingText || config.loadingText;
    this.clearAllText = this.clearAllText || config.clearAllText;
    this.virtualScroll = isDefined(this.virtualScroll)
      ? this.virtualScroll
      : isDefined(config.disableVirtualScroll)
      ? !config.disableVirtualScroll
      : false;
    this.openOnEnter = isDefined(this.openOnEnter) ? this.openOnEnter : config.openOnEnter;
  }
}
